/**
 * Auth Service — BFF with HttpOnly cookies
 *
 * Authentication is handled server-side. Tokens never touch the browser.
 * All requests include credentials for cookie-based session.
 */

import type { User } from "../../types";
import type { AuthService } from "./authService";
import { bffAuthService } from "./bffAuthService";

// Re-export for Login page
export { NewPasswordRequiredError } from "./bffAuthService";

const service: AuthService = bffAuthService;

export const authService: AuthService = {
	async login(email: string, password: string): Promise<User> {
		return service.login(email, password);
	},

	async completeNewPassword(newPassword: string): Promise<User> {
		return service.completeNewPassword!(newPassword);
	},

	async logout(): Promise<void> {
		return service.logout();
	},

	async getCurrentUser(): Promise<User | null> {
		return service.getCurrentUser();
	},

	async signup(email: string, password: string, name: string): Promise<User> {
		return service.signup(email, password, name);
	},

	async createUser(
		email: string,
		password: string,
		name: string,
		role: "admin" | "partner",
	): Promise<User> {
		return service.createUser(email, password, name, role);
	},

	async updateUser(userId: string, updates: Partial<User>): Promise<User> {
		return service.updateUser(userId, updates);
	},

	async getAllUsers(): Promise<User[]> {
		return service.getAllUsers();
	},

	async deleteUser(userId: string): Promise<void> {
		return service.deleteUser(userId);
	},

	async resetUserPassword(
		userId: string,
		password: string,
		permanent?: boolean,
	): Promise<void> {
		return service.resetUserPassword!(userId, password, permanent);
	},

	async forgotPassword(email: string): Promise<void> {
		return service.forgotPassword!(email);
	},

	async confirmForgotPassword(
		email: string,
		code: string,
		newPassword: string,
	): Promise<void> {
		return service.confirmForgotPassword!(email, code, newPassword);
	},
};
